export function lineIntersection(p1, p2, p3, p4) {
  const x1 = p1.x,
    y1 = p1.y,
    x2 = p2.x,
    y2 = p2.y,
    x3 = p3.x,
    y3 = p3.y,
    x4 = p4.x,
    y4 = p4.y;
  const denom = (y4 - y3) * (x2 - x1) - (x4 - x3) * (y2 - y1);
  if (denom === 0) return null; // parallel lines

  const ua = ((x4 - x3) * (y1 - y3) - (y4 - y3) * (x1 - x3)) / denom;
  const ub = ((x2 - x1) * (y1 - y3) - (y2 - y1) * (x1 - x3)) / denom;

  if (ua < 0 || ua > 1 || ub < 0 || ub > 1) return null; // intersection point isn't within both line segments

  return { x: x1 + ua * (x2 - x1), y: y1 + ua * (y2 - y1) };
}

export function isValidEdge(p1, p2, lines, allVertices) {
  // Check if the points lie on a line segment
  const onLine = lines.some(
    ([start, end]) =>
      pointOnLineSegment(p1, start, end) && pointOnLineSegment(p2, start, end),
  );

  if (!onLine) return false;

  // Check if there's no point between p1 and p2
  return !allVertices.some(
    (p) => p !== p1 && p !== p2 && pointOnLineSegment(p, p1, p2),
  );
}

export function pointOnLineSegment(p, lineStart, lineEnd) {
  const d1 = distance(p, lineStart);
  const d2 = distance(p, lineEnd);
  const lineLength = distance(lineStart, lineEnd);
  const buffer = 0.00001; // to account for floating-point errors
  return Math.abs(d1 + d2 - lineLength) < buffer;
}

export function distance(p1, p2) {
  return Math.sqrt((p1.x - p2.x) ** 2 + (p1.y - p2.y) ** 2);
}

export function findCycles(vertices, edges) {
  const adjacencyList = Array(vertices.length)
    .fill()
    .map(() => []);
  edges.forEach(([v1, v2]) => {
    adjacencyList[v1].push(v2);
    adjacencyList[v2].push(v1);
  });

  adjacencyList.forEach((neighbors, vertex) => {
    neighbors.sort(
      (a, b) =>
        angleBetween(vertices[vertex], vertices[a]) -
        angleBetween(vertices[vertex], vertices[b]),
    );
  });

  const visitedEdges = new Set();
  const faces = [];

  for (let start = 0; start < vertices.length; start++) {
    for (const next of adjacencyList[start]) {
      const firstEdgeKey = edgeKey(start, next);
      if (visitedEdges.has(firstEdgeKey)) {
        continue;
      }

      const face = [];
      let from = start;
      let to = next;

      while (!visitedEdges.has(edgeKey(from, to))) {
        visitedEdges.add(edgeKey(from, to));
        face.push(from);

        const neighbors = adjacencyList[to];
        const fromIndex = neighbors.indexOf(from);
        const nextIndex = (fromIndex - 1 + neighbors.length) % neighbors.length;
        const nextVertex = neighbors[nextIndex];

        from = to;
        to = nextVertex;
      }

      if (face.length > 2 && polygonArea(vertices, face) > 0.00001) {
        faces.push(face);
      }
    }
  }

  return faces;
}

function edgeKey(from, to) {
  return `${from}-${to}`;
}

function angleBetween(from, to) {
  return Math.atan2(to.y - from.y, to.x - from.x);
}

function polygonArea(vertices, face) {
  let area = 0;
  for (let i = 0; i < face.length; i++) {
    const current = vertices[face[i]];
    const next = vertices[face[(i + 1) % face.length]];
    area += current.x * next.y - next.x * current.y;
  }

  return area / 2;
}